import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ConfigureTab from "@/components/assistentes/tabs/ConfigureTab";
import PromptTab from "@/components/assistentes/tabs/PromptTab";
import AssistantSimulationChat from "@/components/assistentes/AssistantSimulationChat";
import { Button } from "@/components/ui/button";
import { useAssistants } from "@/hooks/useAssistants";
import { DEFAULT_ASSISTANT_RESPONSE_LANGUAGE } from "@/lib/assistantLanguage";
import { DEFAULT_MESSAGE_SPLIT_STYLE } from "@/lib/messageSplitStyle";
import { DEFAULT_AI_MODEL } from "@/lib/aiModels";
import { useTranslation } from "react-i18next";

const AssistenteNovo: React.FC = () => {
  const { t } = useTranslation("assistants");
  const navigate = useNavigate();
  const { createAssistant } = useAssistants();

  const [activeTab, setActiveTab] = useState("configure");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    model: DEFAULT_AI_MODEL,
    response_language: DEFAULT_ASSISTANT_RESPONSE_LANGUAGE,
    message_split_style: DEFAULT_MESSAGE_SPLIT_STYLE,
    response_time_value: 10,
    response_time_unit: 'seconds',
    max_messages: 1,
    prompt_mode: 'guided-mode',
    default_prompt: '',
    voice_tone: '',
    main_objective: '',
    language_rules: '',
    good_examples: '',
    restrictions: '',
    additional_info: '',
    is_active: true,
    disable_assistant_on_manual_message: false,
  });

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setMessage({ type: 'error', text: t('editor.errNameRequired') });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const result = await createAssistant({
        ...formData,
        response_time_unit: 'seconds',
      });

      if (result.success && result.id) {
        setMessage({ type: 'success', text: t('editor.created') });
        setTimeout(() => {
          navigate(`/assistentes/editar/${result.id}`);
        }, 1000);
      } else {
        setMessage({ type: 'error', text: t('editor.errCreateFailed') });
      }
    } catch (error) {
      console.error('Erro ao criar assistente:', error);
      setMessage({ type: 'error', text: t('editor.errCreate') });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full h-[calc(100dvh-120px)] flex flex-col">
      <div className="mb-4 flex items-center gap-4">
        <button
          onClick={() => navigate('/assistentes')}
          className="flex items-center gap-2 rounded-xl px-2 py-1.5 text-[#334155] hover:bg-[#F1F5F9] hover:text-[#2563EB] transition-colors duration-200 dark:text-[#D4D4D8] dark:hover:bg-[#27272A] dark:hover:text-[#60A5FA]"
        >
          <ArrowLeft size={20} />
          <span>{t("common.back")}</span>
        </button>
        <h2 className="text-2xl xl:text-3xl font-bold tracking-tight text-[#0F172A] dark:text-[#FAFAFA]">
          {t("editor.newTitle")}
        </h2>
      </div>

      {message && (
        <div className={`p-3 rounded-xl mb-4 border ${
          message.type === 'success'
            ? 'bg-[#ECFDF5] border-green-200 text-[#16A34A] dark:bg-green-500/10 dark:border-green-500/30 dark:text-[#22C55E]'
            : 'bg-[#FEF2F2] border-red-200 text-[#DC2626] dark:bg-red-500/10 dark:border-red-500/30 dark:text-[#F87171]'
        }`}>
          {message.text}
        </div>
      )}

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-12 gap-4 xl:gap-6">
        <div className="lg:col-span-8 flex flex-col min-h-0">
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white p-4 md:p-5 shadow-sm dark:border-[#3F3F464D] dark:bg-[#20202040] dark:shadow-none">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col gap-4 min-h-0">
              <TabsList className="mb-0 shrink-0">
                <TabsTrigger value="configure">{t("editor.tabConfigure")}</TabsTrigger>
                <TabsTrigger value="prompt">{t("editor.tabPrompt")}</TabsTrigger>
                <TabsTrigger
                  value="knowledge-base"
                  disabled
                  className="opacity-50 cursor-not-allowed flex items-center gap-1.5"
                  title={t("editor.availableAfterCreate")}
                >
                  <Lock size={12} />
                  {t("editor.tabKnowledge")}
                </TabsTrigger>
                <TabsTrigger
                  value="integrations"
                  disabled
                  className="opacity-50 cursor-not-allowed flex items-center gap-1.5"
                  title={t("editor.availableAfterCreate")}
                >
                  <Lock size={12} />
                  {t("editor.tabIntegrations")}
                </TabsTrigger>
                <TabsTrigger
                  value="webhooks"
                  disabled
                  className="opacity-50 cursor-not-allowed flex items-center gap-1.5"
                  title={t("editor.availableAfterCreate")}
                >
                  <Lock size={12} />
                  {t("editor.tabWebhooks")}
                </TabsTrigger>
              </TabsList>

              <div className="flex-1 overflow-y-auto min-h-0">
                <TabsContent value="configure" className="mt-0">
                  <ConfigureTab formData={formData} onFormChange={handleFormChange} />
                </TabsContent>
                <TabsContent value="prompt" className="mt-0">
                  <PromptTab formData={formData} onFormChange={handleFormChange} />
                </TabsContent>
              </div>
            </Tabs>

            {(activeTab === "configure" || activeTab === "prompt") && (
              <div className="mt-4 pt-4 border-t border-[#E5E7EB] dark:border-[#3F3F464D]">
                <Button
                  onClick={handleSave}
                  disabled={loading}
                  className="w-[120px] rounded-xl bg-[#2563EB] text-white shadow-sm hover:bg-[#1D4ED8] transition-colors duration-200 dark:bg-[#2563EB] dark:hover:bg-[#1D4ED8]"
                >
                  {loading ? t('editor.creating') : t('editor.create')}
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-4 h-full min-h-[500px] lg:min-h-0">
          <AssistantSimulationChat assistant={formData as any} />
        </div>
      </div>
    </div>
  );
};

export default AssistenteNovo;
